import React, { useMemo, useCallback, useState, useRef, useEffect } from "react";
import { colors2, getVal, UI_CAPTION } from "oolib";
import { AnimatePresence } from "framer-motion";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";
import TooltipV2 from "@/components/UI/Tooltip";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import { SearchableLightbox } from "@/components/DataFieldInputSelector/SearchableLightbox";
import { CascadeFieldPicker } from "@/components/DataFieldInputSelector/CascadeFieldPicker";
import { getPickerProp } from "../../utils/pickerVariantConfig";
import { useGetAllOptionsForDimension2 } from "../../WidgetBarChart/utils/useGetAllOptionsForDimension2";
import { NumberFieldConfig } from "../../WidgetBarChart/ConfigForm/NumberFieldConfig";
import { CollapseTransition, CollapseTransitionItem } from "../../utils/CollapseTransition";
import { usePieChartConfig } from "./ConfigForm_Context";
import { DATA_KEYS } from "./DATA_KEYS";
import { useReportLandingContext } from "../../../../ReportLandingContext";
import { useVisibilityModifier } from "../../utils/useVisibilityModifier";

const LAYER_LETTERS = ['A', 'B', 'C', 'D'];

/**
 * Layer A — first dimension. Uses optionsForDim1 (all fields).
 */
const MultiLayerItemA = ({ showIndicator, onChangeValue }) => {
  const {
    widgetConfig,
    dataKeys,
    setConfig,
    underEditWidgetIdx,
    optionsForDim1,
    handleDimension1Change,
  } = usePieChartConfig();

  const dimValue = getVal(widgetConfig, dataKeys.dimension1);

  const { widgetDataStore } = useReportLandingContext();
  const data = widgetDataStore?.[widgetConfig.id];

  const updateConfig = useCallback((updaterFn) => {
    setConfig((prev) => {
      let w = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      w = updaterFn(w);
      const full = [...prev];
      full[underEditWidgetIdx] = w;
      return full;
    });
  }, [setConfig, underEditWidgetIdx]);

  const visibilityModifier = useVisibilityModifier({
    dimension: 1,
    widgetConfig,
    dataKeys: DATA_KEYS,
    data,
    isNumberInput: dimValue?.comp === 'NumberInput',
    updateConfig,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      <DataFieldInputSelector
        id={dataKeys.dimension1}
        indicator={showIndicator ? { label: 'A' } : undefined}
        options={optionsForDim1}
        value={dimValue}
        onChange={(k, v) => {
          handleDimension1Change(v);
          onChangeValue();
        }}
        actions={dimValue ? [{ icon: 'X', onClick: () => {
          handleDimension1Change(null);
          onChangeValue();
        }, hoverColor: 'red' }] : []}
        placeholder="Select field"
        modifier={visibilityModifier}
        picker={getPickerProp({ widgetComp: 'WidgetPieChart', slot: 'layerA' })}
      />
      <NumberFieldConfig
        dimensionKey="dimension1"
        dimensionValue={dimValue}
        dataKeys={DATA_KEYS}
        thisWidgetConfig={widgetConfig}
        setConfig={setConfig}
        underEditWidgetIdx={underEditWidgetIdx}
      />
    </div>
  );
};

/**
 * Layers B, C, D — dimensions 2-4. Cascading: options depend on previous dimension.
 * Same logic as old ConfigForm_DimensionNSection.
 */
const MultiLayerItemN = ({ dimNum, letter, showIndicator, onClear, onChangeValue }) => {
  const {
    widgetConfig,
    dataKeys,
    setConfig,
    underEditWidgetIdx,
    dim1IsGroupQuestion,
    handleDimensionChange,
    handleDimensionClear,
  } = usePieChartConfig();

  const prevDimValue = getVal(widgetConfig, dataKeys[`dimension${dimNum - 1}`]);
  const dimValue = getVal(widgetConfig, dataKeys[`dimension${dimNum}`]);

  const dimOptions = useGetAllOptionsForDimension2({ dimension1Value: prevDimValue });

  const { widgetDataStore } = useReportLandingContext();
  const data = widgetDataStore?.[widgetConfig.id];

  const updateConfig = useCallback((updaterFn) => {
    setConfig((prev) => {
      let w = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      w = updaterFn(w);
      const full = [...prev];
      full[underEditWidgetIdx] = w;
      return full;
    });
  }, [setConfig, underEditWidgetIdx]);

  const visibilityModifier = useVisibilityModifier({
    dimension: dimNum,
    widgetConfig,
    dataKeys: DATA_KEYS,
    data,
    isNumberInput: dimValue?.comp === 'NumberInput',
    updateConfig,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      <CollapseTransition isVisible={dim1IsGroupQuestion}>
        <div style={{ borderRadius: '6px', padding: '1rem', marginBottom: '0.4rem', backgroundColor: colors2.redBG }}>
          <UI_CAPTION style={{ color: colors2.red }}>
            Currently, if a question that was in grouped radio select format is selected, breakdown is not possible against it
          </UI_CAPTION>
        </div>
      </CollapseTransition>

      <DataFieldInputSelector
        id={dataKeys[`dimension${dimNum}`]}
        indicator={showIndicator ? { label: letter } : undefined}
        disabled={dim1IsGroupQuestion || !prevDimValue}
        options={dimOptions}
        value={dimValue}
        onChange={(k, v) => {
          handleDimensionChange(dimNum, v);
          onChangeValue();
        }}
        actions={[{ icon: 'X', onClick: () => {
          handleDimensionClear(dimNum);
          onClear();
        }, hoverColor: 'red' }]}
        placeholder="Select field"
        modifier={visibilityModifier}
        picker={getPickerProp({ widgetComp: 'WidgetPieChart', slot: 'layersBD' })}
        {...(!prevDimValue && !dim1IsGroupQuestion ? { tooltip: { text: `Select layer ${LAYER_LETTERS[dimNum - 2]} first` } } : {})}
      />

      <CollapseTransition isVisible={!!dimValue}>
        <NumberFieldConfig
          dimensionKey={`dimension${dimNum}`}
          dimensionValue={dimValue}
          dataKeys={DATA_KEYS}
          thisWidgetConfig={widgetConfig}
          setConfig={setConfig}
          underEditWidgetIdx={underEditWidgetIdx}
        />
      </CollapseTransition>
    </div>
  );
};

/**
 * Multi-layer section content.
 * - Layer A always visible
 * - Layers B/C/D appear when "+" is clicked
 * - All cascading logic preserved
 */
export const MultiLayerSection = () => {
  const { widgetConfig, dataKeys } = usePieChartConfig();

  // Count how many dimensions are currently filled
  const filledCount = useMemo(() => {
    let count = 0;
    for (let i = 1; i <= 4; i++) {
      if (getVal(widgetConfig, dataKeys[`dimension${i}`])) count = i;
      else break;
    }
    return count;
  }, [widgetConfig, dataKeys]);

  // Visible slots: at least 1 (layer A), at least filledCount
  const [visibleSlots, setVisibleSlots] = useState(() => Math.max(filledCount, 1));

  // Sync visible slots up when dimensions are filled externally (e.g. backward compat)
  useEffect(() => {
    if (filledCount > visibleSlots) {
      setVisibleSlots(filledCount);
    }
  }, [filledCount]);

  const showIndicator = visibleSlots > 1;

  const handleLayerClear = (dimNum) => {
    // Clearing layer N also clears all downstream layers (handled by context),
    // so collapse visible slots back to N-1 (at minimum 1 for layer A)
    if (dimNum > 1) {
      setVisibleSlots(dimNum - 1);
    }
  };

  // When a layer value changes, downstream data is cleared by context,
  // so collapse visible slots to only show up to the changed layer
  const handleLayerChange = (dimNum) => {
    if (visibleSlots > dimNum) {
      setVisibleSlots(dimNum);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <MultiLayerItemA showIndicator={showIndicator} onChangeValue={() => handleLayerChange(1)} />
      <AnimatePresence initial={false}>
        {visibleSlots >= 2 && (
          <CollapseTransitionItem key="layer-B" id="layer-B">
            <MultiLayerItemN dimNum={2} letter="B" showIndicator={showIndicator} onClear={() => handleLayerClear(2)} onChangeValue={() => handleLayerChange(2)} />
          </CollapseTransitionItem>
        )}
        {visibleSlots >= 3 && (
          <CollapseTransitionItem key="layer-C" id="layer-C">
            <MultiLayerItemN dimNum={3} letter="C" showIndicator={showIndicator} onClear={() => handleLayerClear(3)} onChangeValue={() => handleLayerChange(3)} />
          </CollapseTransitionItem>
        )}
        {visibleSlots >= 4 && (
          <CollapseTransitionItem key="layer-D" id="layer-D">
            <MultiLayerItemN dimNum={4} letter="D" showIndicator={showIndicator} onClear={() => handleLayerClear(4)} onChangeValue={() => handleLayerChange(4)} />
          </CollapseTransitionItem>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * "+" button for adding a new layer slot.
 * Opens a lightbox to pick a field for the new slot.
 * Disabled when all 4 slots are visible or when the last visible layer has no value.
 */
export const MultiLayerAddButton = () => {
  const {
    widgetConfig,
    dataKeys,
    dim1IsGroupQuestion,
    handleDimensionChange,
  } = usePieChartConfig();

  // We need to read visibleSlots from MultiLayerSection — but since it's local state there,
  // we'll use a simpler approach: check which is the last filled dimension and add one after it.
  // The "+" always targets the next empty slot after the last filled one.
  const lastFilledDim = useMemo(() => {
    for (let i = 4; i >= 1; i--) {
      if (getVal(widgetConfig, dataKeys[`dimension${i}`])) return i;
    }
    return 0;
  }, [widgetConfig, dataKeys]);

  const nextSlot = lastFilledDim + 1;
  const canAdd = nextSlot <= 4 && lastFilledDim >= 1 && !dim1IsGroupQuestion;

  // Get options for the next slot (cascading from previous dimension)
  const prevDimValue = lastFilledDim > 0 ? getVal(widgetConfig, dataKeys[`dimension${lastFilledDim}`]) : null;
  const nextSlotOptions = useGetAllOptionsForDimension2({
    dimension1Value: canAdd ? prevDimValue : null,
  });

  const [showLightbox, setShowLightbox] = useState(false);
  const btnRef = useRef(null);

  const tooltipText = !canAdd
    ? lastFilledDim === 0
      ? "Select a field first"
      : lastFilledDim >= 4
        ? "Maximum 4 layers reached"
        : dim1IsGroupQuestion
          ? "Breakdown not possible for grouped radio select"
          : "Add a data layer"
    : "Add a data layer";

  return (
    <>
      <TooltipV2 text={tooltipText} placement="top">
        <span ref={btnRef}>
          <ButtonGhost
            icon="Plus"
            onClick={() => setShowLightbox(true)}
            disabled={!canAdd}
            style={{ padding: "4px" }}
          />
        </span>
      </TooltipV2>
      {showLightbox && (() => {
        const handlePick = (selectedField) => {
          let enriched = selectedField;
          if (selectedField && !selectedField.categoryLabel) {
            const group = nextSlotOptions.find(g =>
              Array.isArray(g.options) && g.options.some(o => o.value === selectedField.value)
            );
            if (group) enriched = { ...selectedField, categoryLabel: group.display };
          }
          handleDimensionChange(nextSlot, enriched);
          setShowLightbox(false);
        };
        const cascade = getPickerProp({ widgetComp: 'WidgetPieChart', slot: 'layersBD' });
        return cascade ? (
          <CascadeFieldPicker
            anchorEl={btnRef.current}
            open
            onOpenChange={(v) => { if (!v) setShowLightbox(false); }}
            options={nextSlotOptions}
            value={undefined}
            onChange={handlePick}
          />
        ) : (
          <SearchableLightbox
            anchorRef={btnRef}
            onClose={() => setShowLightbox(false)}
            options={nextSlotOptions}
            grouped={nextSlotOptions.some(item => item.options && Array.isArray(item.options))}
            value={undefined}
            onChange={(_, selectedField) => handlePick(selectedField)}
            searchPlaceholder="Search fields"
          />
        );
      })()}
    </>
  );
};
